import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import type { LogicGraph, SimulationNode } from './LogicGraph';

/** Keep small updates sparse; large waves deduplicate integer slots, not IDs. */
export class ProcessedNodes implements ReadonlySet<NodeHandle> {
  #ids: NodeHandle[] = [];
  #small: Set<NodeHandle> | undefined = new Set();
  #marks?: Uint8Array;
  #revision: number;
  #fallback: boolean;
  #graph?: LogicGraph;
  #weakGraph?: WeakRef<LogicGraph>;
  constructor(graph: LogicGraph) {
    this.#graph = graph;
    this.#revision = graph.topologyRevision;
    this.#fallback = !graph.isTopologyTracked;
  }

  addNode(entry: SimulationNode, graph: LogicGraph): void {
    if (
      !this.#fallback &&
      (graph !== this.#graph || graph.topologyRevision !== this.#revision)
    ) {
      this.#fallback = true;
      this.#small = new Set(this.#ids);
      this.#marks = undefined;
    }
    if (this.#marks) {
      if (this.#marks[entry.slot]) return;
      this.#marks[entry.slot] = 1;
      this.#ids.push(entry.id);
      return;
    }
    const small = this.#small!;
    const before = small.size;
    small.add(entry.id);
    if (small.size === before) return;
    this.#ids.push(entry.id);
    if (!this.#fallback && small.size > 128) {
      this.#marks = new Uint8Array(graph.nodes.size);
      for (const id of this.#ids) this.#marks[graph.peekSimulationNode(id)!.slot] = 1;
      this.#small = undefined;
    }
  }

  finish(): void {
    // A listener retaining a result must not keep an obsolete graph alive.
    if (this.#marks && this.#graph) {
      if (typeof WeakRef !== 'undefined') this.#weakGraph = new WeakRef(this.#graph);
      else this.#materialize();
    }
    this.#graph = undefined;
  }
  #materialize(): Set<NodeHandle> {
    this.#fallback = true;
    this.#marks = undefined;
    return (this.#small ??= new Set(this.#ids));
  }
  union<U>(other: ReadonlySetLike<U>): Set<NodeHandle | U> {
    return this.#materialize().union(other);
  }
  intersection<U>(other: ReadonlySetLike<U>): Set<NodeHandle & U> {
    return this.#materialize().intersection(other);
  }
  difference<U>(other: ReadonlySetLike<U>): Set<NodeHandle> {
    return this.#materialize().difference(other);
  }
  symmetricDifference<U>(other: ReadonlySetLike<U>): Set<NodeHandle | U> {
    return this.#materialize().symmetricDifference(other);
  }
  isSubsetOf(other: ReadonlySetLike<unknown>): boolean {
    return this.#materialize().isSubsetOf(other);
  }
  isSupersetOf(other: ReadonlySetLike<unknown>): boolean {
    return this.#materialize().isSupersetOf(other);
  }
  isDisjointFrom(other: ReadonlySetLike<unknown>): boolean {
    return this.#materialize().isDisjointFrom(other);
  }

  get size(): number {
    return this.#ids.length;
  }
  get [Symbol.toStringTag](): string {
    return 'Set';
  }
  has(id: NodeHandle): boolean {
    if (this.#small) return this.#small.has(id);
    const graph = this.#graph ?? this.#weakGraph?.deref();
    if (graph?.topologyRevision === this.#revision) {
      const entry = graph.peekSimulationNode(id);
      return entry !== undefined && this.#marks![entry.slot] === 1;
    }
    // Published results remain valid even after the graph changes later.
    return this.#materialize().has(id);
  }
  values(): SetIterator<NodeHandle> {
    return this.#ids.values();
  }
  keys(): SetIterator<NodeHandle> {
    return this.values();
  }
  *entries(): SetIterator<[NodeHandle, NodeHandle]> {
    for (const id of this.#ids) yield [id, id];
  }
  [Symbol.iterator](): SetIterator<NodeHandle> {
    return this.values();
  }
  forEach(
    callback: (value: NodeHandle, key: NodeHandle, set: ReadonlySet<NodeHandle>) => void,
    thisArg?: unknown,
  ): void {
    for (const id of this.#ids) callback.call(thisArg, id, id, this);
  }
}
